import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { useConcept } from "@/hooks/useConcepts";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { trackEvent } from "@/lib/engagement";
import type { RefindBlame } from "@/lib/setup/refind";
import { resumeStep, type SetupStep } from "@/lib/setup/resume";
import { useSetupStore } from "@/stores/setup-store";
import { AngleStep } from "./AngleStep";
import { BuildStep } from "./BuildStep";
import { SampleStep } from "./SampleStep";
import { SeedsStep } from "./SeedsStep";
import { SetupStepper } from "./SetupStepper";

/** ④ (the build) is page-local: resume never lands on it — a reload restarts from `resumeStep`. */
type PageStep = SetupStep | 4;

export function SetupPage({ resumeConceptId }: { resumeConceptId?: string }) {
  const navigate = useNavigate();
  const { data: me } = useCurrentUser();
  const [conceptId, setConceptId] = useState<string | undefined>(resumeConceptId);
  const { data: concept } = useConcept(conceptId);
  const [step, setStep] = useState<PageStep | null>(resumeConceptId ? null : 1);
  const [skipped, setSkipped] = useState(false);
  // ③ → ② "re-find" hand-off (spec 2026-10-31 §2); cleared whenever ② is left
  // or reached any other way.
  const [refind, setRefind] = useState<{ blame: RefindBlame | null } | null>(null);
  const startedRef = useRef(false);
  // Step ① hands off to a `?resume=<id>` URL, which remounts this page (route
  // key). That unmount is a continuation, not an abandonment.
  const handedOffRef = useRef(false);
  const stepRef = useRef(step);
  stepRef.current = step;
  const conceptIdRef = useRef(conceptId);
  conceptIdRef.current = conceptId;

  useEffect(() => {
    useSetupStore.getState().begin(resumeConceptId ?? null);
  }, [resumeConceptId]);

  useEffect(() => {
    if (step === null && concept) setStep(resumeStep(concept));
  }, [step, concept]);

  useEffect(() => {
    if (me && !me.features?.setup_v2) navigate({ to: "/dashboard", replace: true });
  }, [me, navigate]);

  // A completed concept belongs in Curate — unless this page started the build:
  // useStartSetup seeds the completed concept into the cache, which would yank ④ away.
  useEffect(() => {
    if (concept?.setup_completed_at && step !== 4) {
      navigate({ to: "/concepts/$conceptId/curate", params: { conceptId: concept.id }, replace: true });
    }
  }, [concept, step, navigate]);

  useEffect(() => () => {
    if (!startedRef.current && !handedOffRef.current) {
      trackEvent("setup_abandoned_at", { step: stepRef.current, concept_id: conceptIdRef.current ?? null, flow: "boards" });
    }
  }, []);

  // Each step starts at the top (② is long; ③ otherwise opens mid-page).
  useEffect(() => { window.scrollTo({ top: 0 }); }, [step]);

  const advance = (from: SetupStep, extra: Record<string, unknown> = {}) => {
    trackEvent("setup_step_completed", { step: from, concept_id: conceptIdRef.current ?? null, flow: "boards", ...extra });
    setRefind(null);
    setStep((from + 1) as SetupStep);
  };

  // Wait for `me` so a non-ring user never sees a flash of setup before the redirect.
  if (!me || step === null || (step > 1 && !concept)) {
    return <Skeleton className="mx-auto mt-8 h-[480px] w-full max-w-5xl" />;
  }

  const backTo = (s: SetupStep) => { setSkipped(false); setRefind(null); setStep(s); };
  return (
    <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-6 px-4 py-6 text-base">
      <header className="flex flex-wrap items-center justify-between gap-4 border-b pb-4">
        <div>
          <p className="text-sm text-muted-foreground">New concept</p>
          <p className="text-lg font-semibold">{concept?.name ?? "Your question"}</p>
        </div>
        <SetupStepper current={step} skipped={skipped} onBack={backTo} />
      </header>
      <main className="min-w-0">
        {step === 1 && (
          <AngleStep
            concept={concept}
            onDone={(id, entry) => {
              conceptIdRef.current = id;
              setConceptId(id);
              useSetupStore.getState().begin(id);
              // Only a new `?resume=` key remounts the page. Back on ① via the
              // stepper, the id is unchanged: no remount, so no hand-off to excuse.
              handedOffRef.current = id !== resumeConceptId;
              navigate({ to: "/concepts/new", search: { resume: id }, replace: true });
              advance(1, { entry });
            }}
          />
        )}
        {step === 2 && concept && (
          <SeedsStep
            concept={concept}
            onDone={() => advance(2)}
            onSkip={() => { setSkipped(true); advance(2, { skipped_sample: true }); }}
            refind={refind ?? undefined}
          />
        )}
        {step === 3 && concept && (
          <SampleStep
            concept={concept}
            autoStart={skipped}
            onRefind={(blame) => { setSkipped(false); setRefind({ blame }); setStep(2); }}
            onStart={() => { startedRef.current = true; setStep(4); }}
          />
        )}
        {step === 4 && concept && (
          <BuildStep
            concept={concept}
            // Un-started again, so leaving a failed build logs setup_abandoned_at.
            onFailed={() => { startedRef.current = false; }}
            onRetry={() => { startedRef.current = true; }}
            onBack={() => backTo(2)}
          />
        )}
      </main>
    </div>
  );
}
